import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { PriorityBadge, FaultStatusBadge, formatNlDate } from "@/components/Badges";
import { Card } from "@/components/ui/card";
import { InlineEmpty } from "@/components/ListState";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

// Storingen list scoped to a single asset (used on the asset detail page).
export const FaultsTab = ({ asset }) => {
  const navigate = useNavigate();
  const [faults, setFaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true); setError("");
      try {
        const { data } = await api.get(`/faults?asset_id=${asset.id}`);
        setFaults(data);
      } catch (e) { setError(getErrorMessage(e, "Kan storingen niet laden.")); }
      finally { setLoading(false); }
    })();
  }, [asset.id]);

  if (loading) return <p className="text-body text-muted-foreground">Laden...</p>;
  if (error) return <p className="text-body text-destructive">{error}</p>;

  return (
    <Card className="p-5">
      <h2 className="mb-3 text-body font-semibold text-foreground">Storingen voor deze asset</h2>
      {faults.length === 0 ? (
        <InlineEmpty
          testid="asset-faults-empty"
          title="Nog geen storingen op deze machine"
          purpose="Elke melding over deze machine komt hier te staan, met prioriteit, status en de datum waarop hij binnenkwam."
        />
      ) : (
        <>
        <div className="hidden sm:block">
          <Table data-testid="asset-faults-table">
            <TableHeader>
              <TableRow>
                <TableHead>Nummer</TableHead>
                <TableHead>Prioriteit</TableHead>
                <TableHead>Omschrijving</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Datum</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {faults.map((f) => (
                <TableRow key={f.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/storingen/${f.id}`)}>
                  <TableCell className="font-mono text-data">{f.fault_number}</TableCell>
                  <TableCell><PriorityBadge priority={f.priority} /></TableCell>
                  <TableCell className="text-body">{f.description}</TableCell>
                  <TableCell><FaultStatusBadge status={f.status} /></TableCell>
                  <TableCell className="text-body">{formatNlDate((f.created_at || "").slice(0, 10))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Phone: one card per storing. */}
        <div className="divide-y divide-border sm:hidden" data-testid="asset-faults-cardlist">
          {faults.map((f) => (
            <button
              key={f.id}
              type="button"
              data-testid={`asset-fault-card-${f.id}`}
              onClick={() => navigate(`/storingen/${f.id}`)}
              className="flex w-full flex-col items-start gap-1 py-3 text-left"
            >
              <div className="flex w-full items-center justify-between gap-2">
                <span className="font-mono text-data text-foreground">{f.fault_number}</span>
                <span className="text-data tabular-nums text-muted-foreground">{formatNlDate((f.created_at || "").slice(0, 10))}</span>
              </div>
              <div className="text-body text-foreground">{f.description}</div>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <PriorityBadge priority={f.priority} />
                <FaultStatusBadge status={f.status} />
              </div>
            </button>
          ))}
        </div>
        </>
      )}
    </Card>
  );
};
